export type MonthlyRepayment = {
  Number: number | string;
  Payment: string;
  Interest: string;
  Principal: string;
  Balance: string;
};

export type YearlyRepayment = {
  Year: number | string;
  totalpayments: string;
  totalinterest: string;
  totalprincipal: string;
  totalbalance: string;
};

export const PAYMENTS_PER_YEAR = {
  monthly: 12,
  fortnightly: 26,
  weekly: 52,
} as const;

export type RepaymentFrequency = keyof typeof PAYMENTS_PER_YEAR;

export const format = (val: number) =>
  parseFloat(val.toFixed(2)).toLocaleString('en-US', {
    minimumFractionDigits: 2,
  });

/** Standard Excel-compatible PMT. pv is the loan amount (positive), fv is the balloon (positive). */
export function pmt(rate: number, nper: number, pv: number, fv = 0): number {
  if (rate === 0) return -(pv + fv) / nper;
  const pow = Math.pow(1 + rate, nper);
  return (-(pv * pow + fv) * rate) / (pow - 1);
}

/** Excel-compatible PV. pmt negative (cash outflow) → returns positive loan amount. */
export function pv(rate: number, nper: number, pmt: number, fv = 0): number {
  if (rate === 0) return -(pmt * nper + fv);
  const pow = Math.pow(1 + rate, nper);
  return -((pmt * (pow - 1)) / (rate * pow) + fv / pow);
}

/** Excel-compatible NPER. pmt negative (cash outflow), pv positive (amount owed). Returns Infinity when payment is insufficient. */
export function nper(rate: number, pmt: number, pv: number, fv = 0): number {
  if (rate === 0) return -(pv + fv) / pmt;
  const ratio = (pmt - fv * rate) / (pmt + pv * rate);
  if (ratio <= 0) return Infinity;
  return Math.log(ratio) / Math.log(1 + rate);
}

export function buildAmortisationSchedule({
  loanAmount,
  periodicRate,
  nPeriods,
  periodicPayment,
  balloonAmount,
  paymentsPerYear,
}: {
  loanAmount: number;
  periodicRate: number;
  nPeriods: number;
  periodicPayment: number;
  balloonAmount: number;
  paymentsPerYear: number;
}): { schedule: MonthlyRepayment[]; yearly: YearlyRepayment[] } {
  let balance = loanAmount;
  const schedule: MonthlyRepayment[] = [];
  const yearly: YearlyRepayment[] = [];
  let yearInterest = 0;
  let yearPayments = 0;
  let yearPrincipal = 0;

  for (let i = 1; i <= nPeriods; i++) {
    const interest = +(balance * periodicRate).toFixed(2);
    const principalPaid = +(periodicPayment - interest).toFixed(2);
    balance = +(balance - principalPaid).toFixed(2);
    yearInterest += interest;
    yearPrincipal += principalPaid;
    yearPayments += periodicPayment;

    schedule.push({
      Number: i,
      Payment: format(periodicPayment),
      Interest: format(interest),
      Principal: format(principalPaid),
      Balance: format(Math.max(balance, 0)),
    });

    if (i % paymentsPerYear === 0 || i === nPeriods) {
      yearly.push({
        Year: i === nPeriods && balloonAmount > 0 ? 'Final' : Math.ceil(i / paymentsPerYear),
        totalpayments: format(yearPayments),
        totalinterest: format(yearInterest),
        totalprincipal: format(yearPrincipal),
        totalbalance: format(Math.max(balance, 0)),
      });
      yearInterest = 0;
      yearPrincipal = 0;
      yearPayments = 0;
    }
  }

  if (balloonAmount > 0) {
    schedule.push({
      Number: 'Balloon',
      Payment: format(balloonAmount),
      Interest: format(0),
      Principal: format(balloonAmount),
      Balance: format(0),
    });
    yearly.push({
      Year: 'Balloon',
      totalpayments: format(balloonAmount),
      totalinterest: format(0),
      totalprincipal: format(balloonAmount),
      totalbalance: format(0),
    });
  }

  return { schedule, yearly };
}
